import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { DocxViewer } from './DocxViewer';
import { XlsxViewer } from './XlsxViewer';

export type FileType = 'pdf' | 'docx' | 'xlsx' | 'office' | 'image' | 'video' | 'audio' | 'html' | 'unsupported';

export interface FileViewerProps {
  fileUrl: string | null;
  fileName: string;
  fileType: FileType;
  loading?: boolean;
  error?: string | null;
  onPageChange?: (page: number) => void;
  onNumPagesChange?: (numPages: number) => void;
  currentPage?: number;
  numPages?: number | null;
  showNavigation?: boolean;
  fullHeight?: boolean;
}

/**
 * Component chung để xem các loại file: PDF, Office, Image, Video, Audio
 * Sử dụng component này thay vì clone code ở nhiều nơi
 */
export const FileViewer: React.FC<FileViewerProps> = ({
  fileUrl,
  fileName,
  fileType,
  loading = false,
  error = null,
  onPageChange,
  onNumPagesChange,
  currentPage = 0,
  numPages = null,
  showNavigation = true,
  fullHeight = false
}) => {
  const [internalPage, setInternalPage] = useState(0);
  const [internalNumPages, setInternalNumPages] = useState<number | null>(null);
  const [iframeLoading, setIframeLoading] = useState(false);

  // Use internal state if props not provided
  const page = currentPage !== undefined ? currentPage : internalPage;
  const totalPages = numPages !== null ? numPages : internalNumPages;

  // IMPORTANT: Hooks must be called unconditionally (before any early returns).
  // When fileUrl changes, reset iframeLoading for iframe-based viewers (pdf/office).
  useEffect(() => {
    if (fileType === 'office' || fileType === 'pdf') {
      setIframeLoading(true);
    } else {
      setIframeLoading(false);
    }
  }, [fileType, fileUrl]);

  const handlePageChange = (newPage: number) => {
    if (onPageChange) {
      onPageChange(newPage);
    } else {
      setInternalPage(newPage);
    }
  };

  const handleNumPagesChange = (newNumPages: number) => {
    if (onNumPagesChange) {
      onNumPagesChange(newNumPages);
    } else {
      setInternalNumPages(newNumPages);
    }
  };

  const handlePreviousPage = () => {
    if (page > 0) {
      handlePageChange(page - 1);
    }
  };

  const handleNextPage = () => {
    if (totalPages && page < totalPages - 1) {
      handlePageChange(page + 1);
    }
  };

  // Get file extension from fileName
  const getFileExtension = (name: string): string => {
    if (!name) return '';
    const index = name.lastIndexOf('.');
    return index !== -1 ? name.substring(index).toLowerCase() : '';
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: '100%',
          height: fullHeight ? '100%' : 'auto',
          minHeight: fullHeight ? '100%' : '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          position: 'relative'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2
          }}
        >
          <CircularProgress size={40} />
          <Typography>Đang tải file...</Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          width: '100%',
          height: fullHeight ? '100%' : 'auto',
          minHeight: fullHeight ? '100%' : '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!fileUrl) {
    return (
      <Box
        sx={{
          width: '100%',
          height: fullHeight ? '100%' : 'auto',
          minHeight: fullHeight ? '100%' : '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3
        }}
      >
        <Alert severity="info">Không có file để hiển thị</Alert>
      </Box>
    );
  }

  // Render based on file type
  return (
    <Box
      sx={{
        width: '100%',
        height: fullHeight ? '100%' : 'auto',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: fileType === 'pdf' ? '#525252' : '#ffffff'
      }}
    >
      {/* PDF Viewer */}
      {fileType === 'pdf' && fileUrl && (
        <>
          <Box sx={{ width: '100%', height: fullHeight ? '100%' : '600px', position: 'relative' }}>
            {iframeLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.85)',
                  zIndex: 10
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={40} />
                  <Typography>Đang tải PDF...</Typography>
                </Box>
              </Box>
            )}

            {/* Use native browser PDF viewer for best performance on large files */}
            <iframe
              src={fileUrl}
              title={fileName}
              style={{ width: '100%', height: '100%', border: 'none', backgroundColor: '#ffffff' }}
              onLoad={() => setIframeLoading(false)}
            />
          </Box>
        </>
      )}

      {/* DOCX Files - Using mammoth.js */}
      {fileType === 'docx' && fileUrl && (
        <DocxViewer
          source={fileUrl}
          fullHeight={fullHeight}
        />
      )}

      {/* XLSX/XLS Files - Using xlsx library */}
      {fileType === 'xlsx' && fileUrl && (
        <XlsxViewer
          source={fileUrl}
          fullHeight={fullHeight}
        />
      )}

      {/* HTML Viewer */}
      {fileType === 'html' && fileUrl && (
        <Box sx={{ width: '100%', height: fullHeight ? '100%' : '600px', position: 'relative' }}>
          <iframe
            src={fileUrl}
            title={fileName}
            style={{ width: '100%', height: '100%', border: 'none', backgroundColor: '#ffffff' }}
          />
        </Box>
      )}

      {/* Other Office Files - iframe viewer (for .doc, .ppt, .pptx) */}
      {fileType === 'office' && fileUrl && (
        <Box sx={{ width: '100%', height: fullHeight ? '100%' : '600px', position: 'relative' }}>
          {iframeLoading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5',
                zIndex: 2
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={40} />
                <Typography>Đang tải tài liệu...</Typography>
              </Box>
            </Box>
          )}
          <iframe
            src={fileUrl}
            width="100%"
            height="100%"
            style={{ border: 'none' }}
            title={`Viewer - ${fileName}`}
            onLoad={() => setIframeLoading(false)}
          />
        </Box>
      )}

      {/* Image Viewer */}
      {fileType === 'image' && fileUrl && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
            backgroundColor: '#f5f5f5'
          }}
        >
          <img
            src={fileUrl}
            alt={fileName}
            style={{
              maxWidth: '100%',
              height: 'auto',
              maxHeight: fullHeight ? '100vh' : '80vh',
              objectFit: 'contain'
            }}
          />
        </Box>
      )}

      {/* Video Viewer */}
      {fileType === 'video' && fileUrl && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
            backgroundColor: '#000'
          }}
        >
          <video
            src={fileUrl}
            controls
            style={{
              width: '100%',
              maxHeight: fullHeight ? '100vh' : '80vh',
              maxWidth: '100%'
            }}
          />
        </Box>
      )}

      {/* Audio Viewer */}
      {fileType === 'audio' && fileUrl && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            backgroundColor: '#f5f5f5'
          }}
        >
          <audio src={fileUrl} controls style={{ width: '100%', maxWidth: '600px' }} />
        </Box>
      )}

      {/* Unsupported file type */}
      {fileType === 'unsupported' && (
        <Box
          sx={{
            width: '100%',
            height: fullHeight ? '100%' : 'auto',
            minHeight: fullHeight ? '100%' : '400px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3
          }}
        >
          <Alert severity="warning">
            Loại file này ({getFileExtension(fileName)}) không được hỗ trợ xem trực tiếp. Vui lòng tải về để xem.
          </Alert>
        </Box>
      )}
    </Box>
  );
};

/**
 * Helper function to determine file type from file name
 */
export const getFileType = (fileName: string): FileType => {
  if (!fileName) return 'unsupported';
  const ext = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();

  if (ext === '.html' || ext === '.htm') return 'html';
  if (ext === '.pdf') return 'pdf';

  // DOCX files - can be rendered with mammoth.js
  if (ext === '.docx') return 'docx';

  // XLSX/XLS files - can be rendered with xlsx library
  if (ext === '.xlsx' || ext === '.xls') return 'xlsx';

  // Other Office files - need external viewer (.doc, .ppt, .pptx not supported client-side)
  const officeExts = ['.doc', '.ppt', '.pptx'];
  if (officeExts.includes(ext)) return 'office';

  const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'];
  if (imageExts.includes(ext)) return 'image';

  const videoExts = ['.mp4', '.avi', '.mov', '.wmv', '.webm'];
  if (videoExts.includes(ext)) return 'video';

  const audioExts = ['.mp3', '.wav', '.flac', '.aac', '.ogg'];
  if (audioExts.includes(ext)) return 'audio';

  return 'unsupported';
};
